import { generate } from 'generate-password';
import { useEffect, useState } from 'react';
import Stripe from 'stripe';

import { ADMIN_JOB_STATUS, ROLE_LIST } from '@common/constants';
import { CLIENT_FORM } from '@common/schema/flow';
import { JOB_FORM } from '@common/schema/job';
import {
  useCreateB2CClientProfileMutation,
  useCreateJobMutation,
  useCreateQuoteMutation,
} from '@controller/schema';
import { cx } from '@emotion/css';
import { processBigCalendarTimeSlots } from '@modules/job/update/processBigCalendarTimeSlots';
import { savePersonalInformation } from '@modules/job/update/savePersonalInformation';
import Big from '@util/big';
import { deleteIndexDBItem, getIndexDBItem } from '@util/indexedDB';
import { fromBase64data } from '@util/saveAndRetrieveImage';

import styles from './PaymentStatus.module.scss';

type Props = {
  paymentIntent: Stripe.PaymentIntent;
};
export const PaymentStatus = (props: Props) => {
  const { mutate } = useCreateB2CClientProfileMutation();
  const jobCreatemutation = useCreateJobMutation();
  const createQuoteMutation = useCreateQuoteMutation();

  const paymentIntent = props.paymentIntent;

  const [message, setMessage] = useState<string | null>(
    'Confirming your payment. Wait a moment...',
  );

  useEffect(() => {
    getIndexDBItem('flowData').then((storedValue) => {
      if (storedValue) {
        const flowData = JSON.parse(storedValue);
        switch (paymentIntent?.status) {
          case 'succeeded':
            createClient(flowData, paymentIntent.id, () => {
              setMessage(
                'Success! Payment received. \nCheck your email for the Order Confirmation.',
              );
            });
            break;

          case 'processing':
            setMessage(
              "Payment processing. We'll update you when payment is received.",
            );
            break;

          case 'requires_payment_method':
            // Redirect your user back to your payment page to attempt collecting
            // payment again
            setMessage('Payment failed. Please try another payment method.');
            break;

          default:
            setMessage('Something went wrong.');
            break;
        }
      }
    });
  }, []);

  const createClient = (
    flowData: any,
    stripePaymentIntentId: string,
    callback: any,
  ) => {
    const cityId = flowData[CLIENT_FORM.CITY].id;
    const address = flowData[CLIENT_FORM.ADDRESS];
    mutate(
      {
        input: {
          errorPath: '',
          isB2B: 0,
          fullName: flowData[CLIENT_FORM.FULL_NAME],
          phoneNumber: flowData[CLIENT_FORM.PHONE_NUMBER],
          email: flowData[CLIENT_FORM.EMAIL],
          address,
          cityId,
          roles: [ROLE_LIST.CLIENT.name],
          password: generate({
            length: 10,
            numbers: true,
            excludeSimilarCharacters: true,
          }),
          stripePaymentIntentId,
          // emailConfirmationLink: "" // TODO: need to send email confirmation about account
        },
      },
      {
        onSuccess: async (result) => {
          const profile = result.createB2CClientProfile.profile;
          // what happens if the email is already taken?
          // we want to fetch existing profile in that case and return that profile
          if (profile) {
            let startTimeSlots: any[] = processBigCalendarTimeSlots(
              flowData[CLIENT_FORM.DATE_AND_TIME],
            );
            const images = flowData[CLIENT_FORM.IMAGES] ?? [];
            const videos = flowData[CLIENT_FORM.VIDEOS] ?? [];

            let problemCategoryId = JSON.parse(
              flowData[JOB_FORM.PROBLEM_CATEGORY_ID],
            );
            let problemSubcategoryId = JSON.parse(
              flowData[JOB_FORM.PROBLEM_SUB_CATEGORY_ID],
            );

            const finalImages = await fromBase64data(images);
            const finalVideos = await fromBase64data(videos);

            const location = flowData[JOB_FORM.LOCATION];
            const postCode = flowData[JOB_FORM.POST_CODE];
            const skillList = flowData[JOB_FORM.SKILL_LIST];
            const skillListInput =
              skillList?.map((skill: any) => +skill.id) ?? [];

            jobCreatemutation.mutate(
              {
                input: {
                  errorPath: '',
                  publishedToApp: false,
                  address,
                  postCode: postCode ?? '',
                  lat: location?.lat ?? 1,
                  lng: location?.lng ?? 1,
                  cityId,
                  clientId: profile.id,
                  // clientCost: flowData[CLIENT_FORM.CLIENT_COST],

                  title: problemSubcategoryId.name,
                  details: flowData[CLIENT_FORM.DESCRIPTION],

                  problemCategoryId: problemCategoryId?.id ?? undefined,
                  problemSubcategoryId: problemSubcategoryId?.id ?? undefined,
                  images: finalImages,
                  videos: finalVideos,
                  startTimeSlots,
                  slotDuration: 8,
                  jobStatusId: ADMIN_JOB_STATUS.PARTIAL_JOB.id,
                  ...savePersonalInformation(flowData),
                  propertyAccess: flowData[JOB_FORM.PROPERTY_ACCESS],
                  referenceId: 'ext',
                  emailConfirmationLink: 'b2c',

                  skillList: skillListInput,
                },
              },
              {
                onSuccess: (result) => {
                  if (result && result.createJob?.job) {
                    createQuoteMutation.mutate({
                      input: {
                        errorPath: '',
                        labourRevenue: new Big(
                          flowData[CLIENT_FORM.CLIENT_COST],
                        ).toNumber(),
                        reportJobId: result.createJob.job.id,
                        isJobReport: true,
                        inputRevenuesManually: true,
                        // totalRevenues: flowData[CLIENT_FORM.CLIENT_COST],
                      },
                    });
                  }
                  deleteIndexDBItem('flowData');
                  callback();
                },
                onError: (error) => {
                  console.log(error);
                },
              },
            );
          }
        },
        onError: (error) => {
          console.log(error);
        },
      },
    );
  };
  // // Retrieve the "payment_intent_client_secret" query parameter appended to
  // // your return_url by Stripe.js
  // const clientSecret = new URLSearchParams(window.location.search).get(
  //   'payment_intent_client_secret',
  // );
  // const clientSecret = props.clientSecret;
  // Retrieve the PaymentIntent
  // stripe
  //   .retrievePaymentIntent(clientSecret as string)
  //   .then(({ paymentIntent }) => {
  // Inspect the PaymentIntent `status` to indicate the status of the payment
  // to your customer.
  //
  // Some payment methods will [immediately succeed or fail][0] upon
  // confirmation, while others will first enter a `processing` state.
  //

  // [0]: https://stripe.com/docs/payments/payment-methods#payment-notification

  return (
    <div className={styles.body}>
      <div className={cx(styles.root, styles.root_form)}>
        <section className={cx(styles.sec, styles.form_wrpr, styles.f_center)}>
          <div className={cx(styles.container, styles.inner)}>
            <div className={styles.row}>
              <div
                className={cx(
                  styles.heading_blk,
                  styles.f_40,
                  styles.text_center,
                )}>
                <h4 className={styles.h4}>{message}</h4>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};
